// src/pages/Collection/CardDetails.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCardDetails } from '../../api/cardService';
import { getCardImageUrl, getElementImageUrl, getCardBackImageUrl } from '../../utils/cardUtils';
import { getCardEffectDescription } from '../../utils/effectFormatter';
import './CardDetails.css';

const CardDetails = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        setLoading(true);
        const response = await getCardDetails(cardId);
        
        if (response.success && response.card) {
          setCard(response.card);
        } else {
          setError('Carte non trouvée');
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des détails de la carte:', err);
        setError('Une erreur est survenue lors du chargement des détails de la carte');
      } finally {
        setLoading(false);
      }
    };

    fetchCardDetails();
  }, [cardId]);

  if (loading) {
    return <div className="card-details-loading">Chargement...</div>;
  }

  if (error || !card) {
    return (
      <div className="card-details-error">
        <h2>Erreur</h2>
        <p>{error || 'Carte non trouvée'}</p>
        <button onClick={() => navigate('/collection')}>Retour à la collection</button>
      </div>
    );
  }

  // Fonction pour obtenir la représentation textuelle de la rareté
  const getRarityText = (rarity) => {
    const rarityMap = {
      'NORMAL': 'Normale',
      'RARE': 'Rare',
      'SUPER_RARE': 'Épique',
      'EPIC': 'Épique',
      'SUPER_SUPER_RARE': 'Légendaire',
      'COMMON': 'Commune',
      'LEGENDARY': 'Légendaire',
      'RAINBOW': 'Arc-en-ciel'
    };
    return rarityMap[rarity] || rarity;
  };

  // Fonction pour obtenir la représentation textuelle du type
  const getTypeText = (type) => {
    const typeMap = {
      'CHARACTER': 'Personnage',
      'SPELL': 'Sort',
      'TRAP': 'Piège',
      'EQUIPMENT': 'Équipement',
      'TERRAIN': 'Terrain'
    };
    return typeMap[type] || type;
  };

  return (
    <div className="card-details-container">
      <div className="card-details-header">
        <button className="back-button" onClick={() => navigate('/collection')}>
          Retour à la collection
        </button>
        <h1>{card.name}</h1>
      </div>
      
      <div className={`card-details-content element-${card.element.toLowerCase()}`}>
        <div className="card-details-image">
          <img
            src={card.imageUrl || `/api/static/images/cards/${card.id}.webp`} 
            alt={card.name} 
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `/api/static/images/elements/${card.element.toLowerCase()}.svg`;
              e.target.onerror = () => {
                e.target.src = `/api/static/images/card-backs/${card.element.toLowerCase()}-back.svg`;
              };
            }}
          />
        </div>
        
        <div className="card-details-info">
          <div className="card-info-group">
            <div className="card-info-item">
              <span className="label">Élément:</span>
              <span className={`value element-${card.element.toLowerCase()}`}>{card.element}</span>
            </div>
            <div className="card-info-item">
              <span className="label">Type:</span>
              <span className="value">{getTypeText(card.type)}</span>
            </div>
            <div className="card-info-item">
              <span className="label">Rareté:</span>
              <span className={`value rarity-${card.rarity.toLowerCase()}`}>{getRarityText(card.rarity)}</span>
            </div>
            <div className="card-info-item">
              <span className="label">Coût:</span>
              <span className="value">{card.cost}</span>
            </div>
            
            {card.type === 'CHARACTER' && (
              <>
                <div className="card-info-item">
                  <span className="label">Attaque:</span>
                  <span className="value">{card.attack}</span>
                </div>
                <div className="card-info-item">
                  <span className="label">Défense:</span>
                  <span className="value">{card.defense}</span>
                </div>
              </>
            )}
          </div>
          
          <div className="card-details-description">
            <h3>Effet</h3>
            <p>{getCardEffectDescription(card)}</p>
            
            {(card.flavorText || card.description) && (
              <>
                <h3>Description</h3>
                <p className="flavor-text">{card.flavorText || card.description}</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardDetails;